// AddTodoForm.js
// Form for adding a new todo

import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import UpsellModal from './UpsellModal';

function AddTodoForm({ onClose, familyId, familyData, currentUserId, userEmail, prefillTitle, isPremium }) {
  // Form state
  const [title, setTitle] = useState(prefillTitle || '');
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignee, setAssignee] = useState('ai'); 

  // Loading state
  const [loading, setLoading] = useState(false);

  // Upsell modal state
  const [showUpsellModal, setShowUpsellModal] = useState(false);

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (title.trim().length === 0) {
      alert('Please enter a title for the todo.');
      return;
    }

    // Check if user is trying to assign to AI without premium
    if (assignee === 'ai' && !isPremium) {
      setShowUpsellModal(true);
      return;
    }

    setLoading(true);

    try {
      // Get assignee name for display
      let assigneeName = '';
      if (assignee === 'ai') {
        assigneeName = 'AI Assistant';
      } else if (assignee && familyData?.memberNames) {
        assigneeName = familyData.memberNames[assignee] || 'Unknown';
      }

      // Create the todo document
      const todoRef = await addDoc(collection(db, 'todos'), {
        familyId: familyId,
        title: title.trim(),
        notes: notes,
        dueDate: dueDate || null,
        assignee: assignee || null,
        assigneeName: assigneeName,
        status: 'pending',
        createdBy: currentUserId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // If assigned to AI, also create an ai_tasks document
      if (assignee === 'ai') {
        await addDoc(collection(db, 'ai_tasks'), {
          todoId: todoRef.id,
          familyId: familyId,
          title: title.trim(),
          details: notes,
          requestedBy: currentUserId,
          requestedByEmail: userEmail,
          status: 'pending',
          createdAt: serverTimestamp()
        });
      }

      // Create notification for the family
      const actorName = familyData?.memberNames?.[currentUserId] || 'Someone';
      await addDoc(collection(db, 'notifications'), {
        familyId: familyId,
        type: 'task_added',
        actorId: currentUserId,
        actorName: actorName,
        taskId: todoRef.id,
        taskTitle: title.trim(),
        readBy: [],
        createdAt: serverTimestamp()
      });

      // Close the form
      onClose();
    } catch (error) {
      console.error('Error creating todo:', error);
      alert('Failed to create todo. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Close form when clicking the overlay (background)
  const handleOverlayClick = (event) => {
    // Only close if clicking directly on the overlay, not the form
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      <div className="add-todo-overlay" onClick={handleOverlayClick}>
        <form className="add-todo-form" onSubmit={handleSubmit}>
          <h2>Add new todo</h2>

          {/* Title field */}
          <div className="form-group">
            <label htmlFor="todo-title">What needs to be done?</label>
            <input
              type="text"
              id="todo-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Pick up kids from school"
              autoFocus
              required
            />
          </div>

          {/* Assignee field - Segmented Control */}
          <div className="form-group">
            <label htmlFor="todo-assignee">Assign to</label>
            <div className="segmented-control">
              {/* AI Assistant option - highlighted */}
              <button
                type="button"
                className={`segmented-control-option ai-option ${assignee === 'ai' ? 'active' : ''}`}
                onClick={() => setAssignee('ai')}
              >
                <span className="segmented-control-icon">🤖</span>
                <span>AI Assistant</span>
                {!isPremium && <span className="premium-lock-badge">✨</span>}
              </button>
              
              {/* Family members */}
              {familyData?.memberNames && Object.entries(familyData.memberNames).map(([userId, firstName]) => (
                <button
                  key={userId}
                  type="button"
                  className={`segmented-control-option ${assignee === userId ? 'active' : ''}`}
                  onClick={() => setAssignee(userId)}
                >
                  {firstName}
                  {userId === currentUserId && <span className="segmented-control-badge">me</span>}
                </button>
              ))}
            </div>
          </div>

          {/* AI Callout - show when AI is selected */}
          {assignee === 'ai' && (
            <div className="ai-callout">
              <div className="ai-callout-title">
                🤖 Assigning to AI Assistant
                {!isPremium && <span className="premium-tag">Premium</span>}
              </div>
              <p>
                <strong>Tip:</strong> More details = better results! Add context below about what you need help with.
              </p>
              <p style={{ marginTop: '8px' }}>
                <strong>How it works:</strong> Kana will reply in the task card with any updates or questions.
              </p>
            </div>
          )}

          {/* Notes field - always visible */}
          <div className="form-group">
            <label htmlFor="todo-notes">
              {assignee === 'ai' ? 'Details for AI' : 'Notes (optional)'}
            </label>
            <textarea
              id="todo-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                assignee === 'ai'
                  ? "e.g., Research summer camps for kids ages 5-8 in Austin area, under $500/week. Need options by next week."
                  : "Add any extra details..."
              }
            />
          </div>

          {/* Due date field */}
          <div className="form-group">
            <label htmlFor="todo-dueDate">Due date (optional)</label>
            <input
              type="date"
              id="todo-dueDate"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          {/* Form buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="button button-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button button-primary"
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add todo'}
            </button>
          </div>
        </form>
      </div>

      {/* Upsell Modal */}
      {showUpsellModal && (
        <UpsellModal onClose={() => setShowUpsellModal(false)} />
      )}
    </>
  );
}

export default AddTodoForm;
